// Person names for activity rows — defense in depth against ids leaking as names.
//
// Producers (auth-service delegation audit) write resolved names into
// metadata.delegatorName / metadata.delegateName and a readable entityName
// ("Jane Doe → John Smith"); older rows only carried entityName, sometimes
// built from raw ids ("<uuid>→<uuid>"). This module resolves what to display in
// that order and REFUSES anything id-shaped, so a UUID or an internal employee
// code is never rendered as a person.

/** Neutral stand-in when a person's name is unknown (mirrors the backend). */
export const UNKNOWN_PERSON = 'a colleague';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Opaque internal identifiers the app already recognises: employee numbers like
// "EMP-0042" / "EMPEF837AE0", ULIDs / long hex-ish tokens, and pure numerics.
const OPAQUE_ID_RE = /^(emp[-_]?[a-z0-9]{3,}|[0-9]{4,}|[0-9a-f]{16,}|[0-9a-z]{26})$/i;

export interface PartyIds {
  delegatorEmployeeId?: string | null;
  delegateEmployeeId?: string | null;
  delegatorUserId?: string | null;
  delegateUserId?: string | null;
}

/** True when a value must never be shown as a name. */
export function isUnsafePersonValue(value: unknown, ids: PartyIds = {}): boolean {
  const v = String(value ?? '').trim();
  if (!v) return true;
  if (UUID_RE.test(v) || OPAQUE_ID_RE.test(v)) return true;
  const known = [ids.delegatorEmployeeId, ids.delegateEmployeeId, ids.delegatorUserId, ids.delegateUserId]
    .filter((x): x is string => !!x)
    .map((x) => x.toLowerCase());
  if (known.includes(v.toLowerCase())) return true;
  // Anything without a single letter (e.g. "→", "123-456") is not a name.
  return !/\p{L}/u.test(v);
}

/** A display-safe person name, or the neutral fallback. */
export function safePersonName(value: unknown, ids: PartyIds = {}): string {
  return isUnsafePersonValue(value, ids) ? UNKNOWN_PERSON : String(value).trim();
}

/** Split a legacy "A → B" / "A→B" entityName into its two halves (raw). */
export function splitEntityName(entityName?: string | null): [string, string] {
  const s = String(entityName ?? '');
  if (!s.includes('→')) return ['', ''];
  const [a, ...rest] = s.split('→');
  return [a.trim(), rest.join('→').trim()];
}

/**
 * Resolve the two parties of a delegation activity row:
 *   1. metadata.delegatorName / metadata.delegateName (preferred, producer-resolved)
 *   2. safe names parsed from entityName ("Jane Doe → John Smith")
 *   3. the neutral fallback
 * Ids in metadata are only used to RECOGNISE unsafe values, never displayed.
 */
export function delegationParties(entityName?: string | null, metadata?: Record<string, unknown> | null): { delegator: string; delegate: string } {
  const meta = (metadata ?? {}) as Record<string, unknown>;
  const ids: PartyIds = {
    delegatorEmployeeId: (meta.delegatorEmployeeId as string) ?? null,
    delegateEmployeeId: (meta.delegateEmployeeId as string) ?? null,
    delegatorUserId: (meta.delegatorUserId as string) ?? null,
    delegateUserId: (meta.delegateUserId as string) ?? null,
  };
  const [legacyDelegator, legacyDelegate] = splitEntityName(entityName);
  const pick = (preferred: unknown, legacy: string) =>
    !isUnsafePersonValue(preferred, ids) ? String(preferred).trim() : safePersonName(legacy, ids);
  return {
    delegator: pick(meta.delegatorName, legacyDelegator),
    delegate: pick(meta.delegateName, legacyDelegate),
  };
}
